import { Injectable, NgModule, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import type { IdTokenResult, ParsedToken, User } from 'firebase/auth';
import { Observable, OperatorFunction, of, pipe } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { Auth, user } from '@fido/firebase/auth';

export type AuthPipe = OperatorFunction<User | null, boolean | string | unknown[]>;

export type AuthPipeGenerator = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => AuthPipe;

function isAuthPipeGenerator(value: unknown): value is AuthPipeGenerator {
  return typeof value === 'function';
}

/** Default pipe — emits true when there's a signed-in user, false otherwise. */
export const loggedIn: AuthPipe = map((u) => u !== null);

@Injectable({ providedIn: 'any' })
export class AuthGuard implements CanActivate {
  private readonly router = inject(Router);
  private readonly auth = inject(Auth);

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    const rawPipe = next.data['authGuardPipe'];
    const authPipeFactory: AuthPipeGenerator = isAuthPipeGenerator(rawPipe)
      ? rawPipe
      : (): AuthPipe => loggedIn;
    return user(this.auth).pipe(
      take(1),
      authPipeFactory(next, state),
      map((can): boolean | UrlTree => {
        if (typeof can === 'boolean') return can;
        if (Array.isArray(can)) return this.router.createUrlTree(can);
        return this.router.parseUrl(can);
      }),
    );
  }
}

export const canActivate = (
  pipe: AuthPipeGenerator,
): { canActivate: (typeof AuthGuard)[]; data: { authGuardPipe: AuthPipeGenerator } } => ({
  canActivate: [AuthGuard],
  data: { authGuardPipe: pipe },
});

export const isNotAnonymous: AuthPipe = map((u) => u !== null && !u.isAnonymous);

export const idTokenResult: OperatorFunction<User | null, IdTokenResult | null> = switchMap((u) =>
  u !== null ? u.getIdTokenResult() : of(null),
);

export const emailVerified: AuthPipe = map((u) => u !== null && u.emailVerified);

export const customClaims: OperatorFunction<User | null, ParsedToken | unknown[]> = pipe(
  idTokenResult,
  map((res): ParsedToken | unknown[] => (res !== null ? res.claims : [])),
);

export const hasCustomClaim = (claim: string): AuthPipe =>
  pipe(
    customClaims,
    map((claims) =>
      Array.isArray(claims) ? false : Object.prototype.hasOwnProperty.call(claims, claim),
    ),
  );

export const redirectUnauthorizedTo = (redirect: string | unknown[]): AuthPipe =>
  pipe(
    loggedIn,
    map((isLoggedIn): boolean | string | unknown[] => (isLoggedIn ? true : redirect)),
  );

export const redirectLoggedInTo = (redirect: string | unknown[]): AuthPipe =>
  pipe(
    loggedIn,
    map((isLoggedIn): boolean | string | unknown[] => (isLoggedIn ? redirect : true)),
  );

@NgModule({
  providers: [AuthGuard],
})
export class AuthGuardModule {}
